// The plan on the dashboard (spec "Public launch"): what you pay and when, changing size or
// monthly/yearly (the price first, then the machine changes with the plan), Stripe's page for
// the card, invoices and cancelling, and what's happening when a payment didn't go through.

import { IconInfoCircle } from "@tabler/icons-react";
import { Button } from '../ui/Button';
import { Tag, type TagTone } from '../ui/Tag';
import { Empty, EmptyArt } from '../ui/ds';
import { useEffect, useState } from "react";
import { billing, openBillingPortal, usd, type Interval, type Plan, type PlanOption } from "../api";
import type { Me } from "../store";
import { Link } from "../router";
import { refreshMe } from "../session";
import { IntervalSwitch } from "./Plans";
import { TierPicker } from "./MachineControls";
import { Dialog } from "../ui/Dialog";

const day = (unix: number) => new Date(unix * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

/** How the plan stands, in a word and a colour. */
function standing(p: Plan): { label: string; tone: TagTone } {
  if (p.stoppedAt) return { label: "Paused", tone: "red" };
  if (p.unpaidSince) return { label: "Payment due", tone: "amber" };
  if (p.status === "canceled") return { label: "Ended", tone: "red" };
  if (p.cancelAtEnd) return { label: "Ending", tone: "amber" };
  return { label: "Active", tone: "green" };
}

/** A button to Stripe's billing page (it leaves this page). */
function PortalButton({ label, primary }: { label: string; primary?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button
        className={`btn ${primary ? "btn-primary" : ""}`}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(await openBillingPortal());
          setBusy(false);
        }}
        data-testid="billing-portal"
      >
        {busy ? "Opening…" : label}
      </button>
      {error && <span className="form-error">{error}</span>}
    </>
  );
}

/** A payment that didn't go through, or a machine paused for it: above everything else. */
export function PlanBanner({ me }: { me: Me }) {
  const p = me.billing.plan;
  if (!p || !(p.unpaidSince || p.stoppedAt)) return null;
  return (
    <div className={`banner ${p.stoppedAt ? "banner-error" : ""} plan-banner`} role="alert" data-testid="plan-banner">
      <div>
        <strong>{p.stoppedAt ? "Your cloud machine is paused." : "Your last payment didn’t go through."}</strong>{" "}
        {p.stoppedAt
          ? `Its disk is kept until ${day(p.deleteAt!)}. Pay before then and it starts as you left it.`
          : `Everything keeps working while we try again. Update your card before ${day(p.pauseAt!)}, or your cloud machine pauses.`}
      </div>
      <PortalButton label="Update your card" primary />
    </div>
  );
}

/** Change size or monthly/yearly: what it costs now first, then done in one click. */
function ChangePlan({ plan, onClose, onDone }: { plan: Plan; onClose: () => void; onDone: (what: string) => void }) {
  const [plans, setPlans] = useState<PlanOption[] | null>(null);
  const [tier, setTier] = useState(plan.tier);
  const [interval, setInterval_] = useState<Interval>(plan.interval);
  const [due, setDue] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string; card?: boolean } | null>(null);
  const same = tier === plan.tier && interval === plan.interval;

  useEffect(() => {
    void billing.get().then((r) => r.ok && setPlans(r.data.plans ?? []));
  }, []);
  // The price of the change, asked again as the choice changes.
  useEffect(() => {
    setDue(null);
    if (same) return;
    let live = true;
    void billing.preview(tier, interval).then((r) => live && r.ok && setDue(r.data.amountDue));
    return () => {
      live = false;
    };
  }, [tier, interval, same]);

  const picked = plans?.find((p) => p.id === tier);
  const price = picked ? usd(interval === "year" ? picked.yearly : picked.monthly) + (interval === "year" ? "/year" : "/month") : "";
  const apply = async () => {
    setBusy(true);
    setMsg(null);
    const r = await billing.change(tier, interval);
    setBusy(false);
    if (r.ok) {
      await refreshMe();
      onDone(`You’re on ${picked?.name}, ${price}. Your cloud machine has its new size.`);
      return;
    }
    setMsg({ ok: false, text: r.error, card: r.status === 402 });
  };

  // the Mac's Change size dialog, with the price: monthly/yearly, the sizes as its cards, what it costs now, Close and Change
  return (
    <Dialog title="Change plan" wide onClose={onClose} testId="plan-change">
      <div className="plan-change-bar">
        <IntervalSwitch value={interval} onChange={setInterval_} />
      </div>
      {plans ? (
        <TierPicker
          choices={{ tiers: plans }}
          tier={tier}
          onTier={setTier}
          current={plan.interval === interval ? plan.tier : undefined}
          price={(id) => {
            const o = plans.find((x) => x.id === id);
            return o ? `${usd(interval === "year" ? o.yearly : o.monthly)}/${interval === "year" ? "yr" : "mo"}` : "";
          }}
        />
      ) : (
        <div className="skeleton" aria-busy="true" aria-label="Loading sizes" />
      )}
      {/* the Change size dialog's note: nothing restarts, then what it costs */}
      <div className="tier-note" role="note">
        <IconInfoCircle size={16} stroke={1.5} aria-hidden="true" />
        <span>
          <strong>Nothing restarts</strong>
          <span aria-live="polite" data-testid="plan-change-price">
            {same
              ? "Pick a size or switch between monthly and yearly."
              : due === null
                ? "Working out the price…"
                : due > 0
                  ? `${usd(due)} now (what’s left of this period, prorated), then ${price}.`
                  : `Nothing to pay now: the difference comes off your next invoices. Then ${price}.`}
          </span>
        </span>
      </div>
      {msg && (
        <div className={msg.ok ? "form-ok" : "form-error"} role="status">
          {msg.text} {msg.card && <PortalButton label="Update your card" />}
        </div>
      )}
      <div className="form-actions">
        <Button onClick={onClose}>Close</Button>
        <Button variant="primary" disabled={busy || same || due === null || !picked?.available} onClick={() => void apply()} data-testid="plan-change-apply">
          {busy ? "Changing…" : due && due > 0 ? `Pay ${usd(due)} and change` : "Change plan"}
        </Button>
      </div>
    </Dialog>
  );
}

export function PlanCard({ me }: { me: Me }) {
  const [changing, setChanging] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(null), 6000);
    return () => clearTimeout(t);
  }, [done]);
  const [names, setNames] = useState<Record<string, PlanOption>>({});
  const p = me.billing.plan;
  useEffect(() => {
    void billing.get().then((r) => r.ok && setNames(Object.fromEntries((r.data.plans ?? []).map((x) => [x.id, x]))));
  }, [p?.tier]);
  // From an email (?billing=1): straight to Stripe's page.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("billing") === "1" && p) {
      history.replaceState(null, "", location.pathname);
      void openBillingPortal();
    }
  }, [p]);

  if (!me.billing.enabled) return null;
  if (!p || p.status === "canceled")
    return (
      <section className="card card-plan" aria-labelledby="plan-title" data-testid="plan-card">
        <div className="card-head">
          <h2 id="plan-title">Plan</h2>
        </div>
        <Empty
          size="md"
          art="machine"
          className="table-empty"
          data-testid="plan-empty"
          title={p ? "Your plan has ended" : "No plan yet"}
          lead="Pick a size to keep a cloud machine running. You can change it any time."
        >
          <Link to="/plans" className="btn btn-primary" data-testid="plan-pick">
            Pick a plan <span aria-hidden="true">→</span>
          </Link>
        </Empty>
      </section>
    );
  const size = names[p.tier];
  const s = standing(p);
  const full = size ? (p.interval === "year" ? size.yearly : size.monthly) : 0;
  const amount = usd(Math.round((full * (100 - (p.off ?? 0))) / 100));
  const per = p.interval === "year" ? "a year" : "a month";
  return (
    <section className="card card-plan" aria-labelledby="plan-title" data-testid="plan-card">
      <div className="card-head">
        <h2 id="plan-title">Plan</h2>
      </div>
      {/* one row, as Claude's plan: the machine drawn in its well, the size with how it stands, what it is, what it
          costs and when it renews; the two buttons at the right */}
      <div className="plan-row">
        <span className="plan-art" aria-hidden="true">
          <EmptyArt kind="machine" size="md" />
        </span>
        <div className="plan-info">
          <span className="plan-name">
            <strong data-testid="plan-size">{size?.name ?? p.tier}</strong>
            <Tag tone={s.tone} data-testid="plan-standing">
              {s.label}
            </Tag>
          </span>
          {size && <span className="plan-specs">{size.vcpu} vCPU · {size.memoryGB} GB memory · {size.diskGB} GB disk</span>}
          <span className="plan-meta">
            {p.founding ? (
              <>Free for good · Founding member</>
            ) : p.off === 100 && !p.offEnd ? (
              "Free for good"
            ) : (
              <span title={p.off ? `${p.off}% off ${p.offEnd ? `until ${day(p.offEnd)}` : "for good"}, then ${usd(full)} ${per}` : undefined}>
                {size ? `${amount} ${per}` : ""}
                {p.off ? ` · ${p.off}% off` : null}
              </span>
            )}
            {p.periodEnd ? ` · ${p.cancelAtEnd ? "Ends" : "Renews"} on ${day(p.periodEnd)}` : ""}
          </span>
        </div>
        <div className="plan-actions">
          <button className="btn" onClick={() => setChanging(true)} disabled={!!p.stoppedAt} data-testid="plan-change-open">
            Change plan
          </button>
          <PortalButton label={p.cancelAtEnd ? "Keep my plan" : "Billing and invoices"} />
        </div>
      </div>
      {p.cancelAtEnd && (
        <p className="muted small">Your cloud machine stops on {day(p.periodEnd)} and its disk is deleted 14 days later. You can keep the plan on the billing page.</p>
      )}
      {done && (
        <div className="form-ok plan-done" role="status" data-testid="plan-done">
          {done}
        </div>
      )}
      {changing && <ChangePlan plan={p} onClose={() => setChanging(false)} onDone={(what) => (setChanging(false), setDone(what))} />}
    </section>
  );
}
